import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger } from '@strivelabs/ui-core';
import { IconButton } from '@strivelabs/ui-core/v2';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ChartWrapper } from './ChartWrapper';
import { LineChart } from './LineChart';
import { AreaChart } from './AreaChart';
import { BarChart } from './BarChart';
import { PieChart } from './PieChart';
import { RadialChart } from './RadialChart';
import { ScatterChart } from './ScatterChart';
import type { LineChartProps } from './LineChart';
import type { AreaChartProps } from './AreaChart';
import type { BarChartProps } from './BarChart';
import type { PieChartProps } from './PieChart';
import type { RadialChartProps } from './RadialChart';
import type { ScatterChartProps } from './ScatterChart';
import type { ChartReferenceLine, ChartReferenceDot, ChartReferenceArea } from '../../types';

/** Props to omit from inner chart types — TabbedChart owns these */
type OmitWrapperProps = 'title' | 'description' | 'expandable' | 'expandedHeight' | 'className';

export type TabbedChartTab =
  | { id: string; label: string; chartType: 'line'; chartProps: Omit<LineChartProps, OmitWrapperProps> }
  | { id: string; label: string; chartType: 'area'; chartProps: Omit<AreaChartProps, OmitWrapperProps> }
  | { id: string; label: string; chartType: 'bar'; chartProps: Omit<BarChartProps, OmitWrapperProps> }
  | { id: string; label: string; chartType: 'pie'; chartProps: Omit<PieChartProps, OmitWrapperProps> }
  | { id: string; label: string; chartType: 'radial'; chartProps: Omit<RadialChartProps, OmitWrapperProps> }
  | { id: string; label: string; chartType: 'scatter'; chartProps: Omit<ScatterChartProps, OmitWrapperProps> };

export interface TabbedChartProps {
  /** Tab definitions — each tab specifies the chart type and its data/config */
  tabs: TabbedChartTab[];
  /** ID of the initially selected tab. Defaults to the first tab. */
  defaultTabId?: string;
  /** Card title rendered above the tab bar */
  title?: string;
  /** Card description rendered below the title */
  description?: string;
  /** Chart height in pixels (default: 300) */
  height?: number;
  /** Additional class name for the outer card */
  className?: string;
  /** Show the expand-to-fullscreen button (default: true) */
  expandable?: boolean;
  /** Chart height inside the expanded dialog (default: 500) */
  expandedHeight?: number;
  /** @internal Set by ChartWrapper when rendering in expanded dialog */
  isExpanded?: boolean;
  /** Vertical/horizontal reference lines (shared across all tabs) */
  referenceLines?: ChartReferenceLine[];
  /** Interactive point markers with optional hover tooltips (shared across all tabs) */
  referenceDots?: ChartReferenceDot[];
  /** Shaded reference regions (shared across all tabs) */
  referenceAreas?: ChartReferenceArea[];
  /**
   * Optional content rendered to the right of the tab list. Use for extra
   * filter controls (multi-selects, toggles) that scope the active chart.
   * The consumer owns the state and renders whatever UI they need.
   */
  actions?: React.ReactNode;
  /**
   * Fires whenever the active tab changes. Useful for conditionally rendering
   * parts of `actions` based on which tab is showing.
   */
  onTabChange?: (tabId: string) => void;
}

// Strips the card appearance from inner charts so they sit flush inside TabbedChart's own card
const BARE_CARD_CLASS = 'border-0 p-0 rounded-none bg-transparent shadow-none';

/** Concatenate two optional arrays, returning undefined when both are empty. */
function mergeRefs<T>(shared?: T[], perTab?: T[]): T[] | undefined {
  if (!shared?.length && !perTab?.length) return undefined;
  return [...(shared || []), ...(perTab || [])];
}

function renderActiveChart(
  tab: TabbedChartTab,
  height: number,
  isExpanded: boolean,
  referenceLines?: ChartReferenceLine[],
  referenceDots?: ChartReferenceDot[],
  referenceAreas?: ChartReferenceArea[],
): React.ReactNode {
  // Merge shared (TabbedChart-level) + per-tab (chartProps-level) reference markers
  const tabProps = tab.chartProps as Record<string, any>;
  const shared = {
    height,
    expandable: false as const,
    isExpanded,
    className: BARE_CARD_CLASS,
    referenceLines: mergeRefs(referenceLines, tabProps.referenceLines),
    referenceDots: mergeRefs(referenceDots, tabProps.referenceDots),
    referenceAreas: mergeRefs(referenceAreas, tabProps.referenceAreas),
  };

  switch (tab.chartType) {
    case 'line':
      return <LineChart {...tab.chartProps} {...shared} />;
    case 'area':
      return <AreaChart {...tab.chartProps} {...shared} />;
    case 'bar':
      return <BarChart {...tab.chartProps} {...shared} />;
    case 'pie':
      return <PieChart {...tab.chartProps} {...shared} />;
    case 'radial':
      return <RadialChart {...tab.chartProps} {...shared} />;
    case 'scatter':
      return <ScatterChart {...tab.chartProps} {...shared} />;
  }
}

export const TabbedChart: React.FC<TabbedChartProps> = ({
  tabs: rawTabs,
  defaultTabId,
  title,
  description,
  height = 300,
  className,
  expandable = true,
  expandedHeight = 500,
  isExpanded = false,
  referenceLines: refLines,
  referenceDots: refDots,
  referenceAreas: refAreas,
  actions,
  onTabChange,
}) => {
  // Ensure every tab has an id — LLMs may omit it
  const tabs = React.useMemo(
    () => rawTabs.map((t, i) => (t.id ? t : { ...t, id: `tab-${i}` })) as TabbedChartTab[],
    [rawTabs],
  );
  const [activeTabId, setActiveTabId] = useState<string>(defaultTabId ?? tabs[0]?.id ?? '');
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateScrollState, tabs]);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.6;
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  const activeTab = tabs.find((t) => t.id === activeTabId) ?? tabs[0];
  const chartHeight = isExpanded ? expandedHeight : height;

  return (
    <ChartWrapper
      title={title}
      description={description}
      className={className}
      expandable={expandable}
      expandedHeight={expandedHeight}
      chartComponent={TabbedChart}
      chartProps={{
        tabs,
        defaultTabId: activeTabId, // preserve active tab when re-rendered in the dialog
        height,
        referenceLines: refLines,
        referenceDots: refDots,
        referenceAreas: refAreas,
        actions,
        onTabChange,
      }}
    >
      <Tabs
        value={activeTabId}
        onValueChange={(id) => {
          setActiveTabId(id);
          onTabChange?.(id);
        }}
      >
        <div className="flex items-center gap-3">
          <div className="relative flex-1 flex items-center min-w-0">
            {canScrollLeft && (
              <IconButton
                variant="secondary"
                size="sm"
                aria-label="Scroll tabs left"
                onClick={() => scroll('left')}
                className="absolute left-0 z-10"
              >
                <ChevronLeft className="h-4 w-4" />
              </IconButton>
            )}

            <div
              ref={scrollRef}
              onScroll={updateScrollState}
              className={`overflow-x-auto scrollbar-none ${canScrollLeft ? 'pl-9' : ''} ${canScrollRight ? 'pr-9' : ''}`}
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              <TabsList className="border border-border shadow-sm w-max">
                {tabs.map((tab) => (
                  <TabsTrigger key={tab.id} value={tab.id}>
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {canScrollRight && (
              <IconButton
                variant="secondary"
                size="sm"
                aria-label="Scroll tabs right"
                onClick={() => scroll('right')}
                className="absolute right-0 z-10"
              >
                <ChevronRight className="h-4 w-4" />
              </IconButton>
            )}
          </div>

          {actions && (
            <div className="flex-shrink-0 flex items-center gap-2">{actions}</div>
          )}
        </div>

        {/* Active chart — rendered without its own card shell */}
        <div className="mt-6">
          {activeTab && renderActiveChart(activeTab, chartHeight, isExpanded, refLines, refDots, refAreas)}
        </div>
      </Tabs>
    </ChartWrapper>
  );
};
